import React from 'react';
import {Box, Button, Grid, Text} from '@chakra-ui/react';
import {
  FieldMappingLabels,
  MultiMarketFields,
  AdditionalFieldMapping,
  FieldOverride,
  AdditionalFieldMappingField,
  CoreAdditionalFieldMappingFields,
  OptionalAdditionalFieldMappingFields,
  isAdditionalFieldMappingField,
  isAdditionalFieldMapping,
  AdditionalKeyplayFieldMapping,
  CustomFieldMapping,
} from '../../shared/integrations';
import {AddAlt, ChevronRight, TrashCan} from '@carbon/icons-react';
import {
  isPlaceholderAdditionalFieldMapping,
  PlaceholderAdditionalFieldMapping,
  useIntegrationDetailsState,
} from './IntegrationDetails.state';
import {useCustomer, useMarkets} from '../../hooks/api/metadata';
import {GridCell} from './components';
import {IntegrationSection} from './components';
import {GridHeader} from './components';
import Select from 'react-select';
import {
  FieldDefinition,
  getFieldLabel,
  isAiNonResearchField,
  isResearchField,
} from '../../shared/enrichment';
import _ from 'lodash';
import {Market} from '../../shared/market';
import {useCrmFields} from '../../hooks/api/integrations';
import {
  useIntegrationFromContext,
  useIntegrationStaticInfo,
} from './IntegrationDetailsContext';
import {
  LabelAndDescriptionData,
  LabelAndDescriptionOption,
} from '../LabelAndDescriptionOption';
import {ObjectId} from 'bson';

export const AdditionalEnrichmentFieldMapping = () => {
  const integration = useIntegrationFromContext();
  const integrationInfo = useIntegrationStaticInfo();

  const {
    additionalFieldMappings,
    addAdditionalFieldMapping,
    managedFieldMappings,
    serverState,
    isSaveableFieldMapping,
  } = useIntegrationDetailsState();
  const crmFieldsResult = useCrmFields(integration.type);
  const markets = useMarkets();
  const customer = useCustomer();
  const validAdditionalFieldMappings = additionalFieldMappings.filter(
    isAdditionalFieldMapping
  );

  const availableFieldMappingFields = CoreAdditionalFieldMappingFields.concat(
    _.intersection(
      (serverState?.config?.optionalFields ?? []).filter(
        isAdditionalFieldMappingField
      ),
      OptionalAdditionalFieldMappingFields
    )
  );

  const existingFieldMappings = new Set([
    ...validAdditionalFieldMappings.map(({crmFieldName}) => crmFieldName),
    ...managedFieldMappings.map(({crmFieldName}) => crmFieldName),
  ]);

  // These are all the enrichment field options to show in the select dropdown
  const fieldMappingOptions = getAllFieldConfigOptions(
    availableFieldMappingFields,
    validAdditionalFieldMappings,
    markets,
    customer.fieldDefinitions ?? []
  );

  // These are all the CRM field options to show in the select dropdown
  const crmFieldOptions =
    crmFieldsResult.data?.map((field) => ({
      isDisabled: existingFieldMappings.has(field.name),
      label: field.label,
      value: field.name,
    })) ?? [];

  const addNewFieldMapping = () => {
    addAdditionalFieldMapping({
      id: new ObjectId(),
      type: 'placeholder',
    });
  };

  const hasMoreFieldsToAdd =
    validAdditionalFieldMappings.length < fieldMappingOptions.length;
  const disableAddToFieldButton =
    !additionalFieldMappings.some(isPlaceholderAdditionalFieldMapping) &&
    validAdditionalFieldMappings.every(isSaveableFieldMapping);

  return (
    <IntegrationSection>
      <Box fontSize={'lg'} fontWeight={500} mb={6}>
        Additional Field Mapping
      </Box>
      <Grid templateColumns={'70px 1fr min-content 1fr 220px'} gap={2}>
        {additionalFieldMappings.length > 0 && (
          <>
            <GridHeader></GridHeader>
            <GridHeader>Keyplay Field</GridHeader>
            <GridHeader></GridHeader>
            <GridHeader>{integrationInfo.label} Field</GridHeader>
            <GridHeader ms="4">Override?</GridHeader>
          </>
        )}

        {additionalFieldMappings.length === 0 && (
          <GridCell colSpan={6} px={0}>
            <Text fontStyle={'italic'}>No field mappings configured.</Text>
          </GridCell>
        )}

        {additionalFieldMappings.map((selectedFieldMapping, index) => (
          <FieldMappingGridRow
            key={index}
            crmFieldOptions={crmFieldOptions}
            fieldMappingOptions={fieldMappingOptions}
            selectedFieldMapping={selectedFieldMapping}
          />
        ))}

        {hasMoreFieldsToAdd && (
          <GridCell colSpan={2} px={2}>
            <Button
              alignItems={'center'}
              colorScheme="kbuttonblue"
              isDisabled={!disableAddToFieldButton}
              fontWeight={500}
              gap={2}
              onClick={addNewFieldMapping}
              variant="link"
            >
              <AddAlt />
              Add a field
            </Button>
          </GridCell>
        )}
      </Grid>
    </IntegrationSection>
  );
};

function getAllFieldConfigOptions(
  fieldMappingFields: AdditionalFieldMappingField[],
  fieldMappings: AdditionalFieldMapping[],
  markets: Market[],
  fieldDefinitions: FieldDefinition[]
): FieldMappingOption[] {
  return [
    // For each additional enrichment field we create an option, except
    // for the multi-market fields, which we handle separately.
    ..._.difference(fieldMappingFields, MultiMarketFields).map((field) => {
      const existingFieldMapping = fieldMappings.find(
        (fieldMapping) =>
          fieldMapping.kind === 'keyplay' &&
          fieldMapping.enrichmentField === field
      );

      return {
        isDisabled: !!existingFieldMapping,
        allowedOverrideOptions: defaultOverrideOptions,
        partialFieldMapping: {
          enrichmentField: field,
          kind: 'keyplay' as const,
        },
        label: FieldMappingLabels[field],
        value: field,
      } satisfies FieldMappingOption;
    }),
    // For each multi-market field, we create an option for each market
    ...MultiMarketFields.map((field) => {
      return markets.map((market) => {
        const existingFieldMapping = fieldMappings.find(
          (fieldMapping) =>
            fieldMapping.kind === 'keyplay' &&
            fieldMapping.enrichmentField === field &&
            fieldMapping.marketId?.equals(market.id)
        );
        const label =
          markets.length > 0
            ? market.label + ' ' + FieldMappingLabels[field]
            : FieldMappingLabels[field];

        return {
          isDisabled: !!existingFieldMapping,
          allowedOverrideOptions: defaultOverrideOptions,
          partialFieldMapping: {
            enrichmentField: field,
            kind: 'keyplay' as const,
            marketId: market.id,
          },
          label,
          value: field + market.id,
        } satisfies FieldMappingOption;
      });
    }).flat(),
    // Create an option for each custom field
    // If a non-research AI field, also create an option for the reasoning
    ...fieldDefinitions
      .filter(({status}) => status === 'published')
      .map((fieldDefinition) => {
        const {id} = fieldDefinition;
        const label = getFieldLabel(fieldDefinition);

        const existingValueFieldMapping = fieldMappings.find(
          (fieldMapping) =>
            fieldMapping.kind === 'custom' &&
            fieldMapping.type === 'value' &&
            fieldMapping.customFieldId.equals(id)
        );
        const existingReasoningFieldMapping = fieldMappings.find(
          (fieldMapping) =>
            fieldMapping.kind === 'custom' &&
            fieldMapping.type === 'reasoning' &&
            fieldMapping.customFieldId.equals(id)
        );

        const isAiResearchField = isResearchField(fieldDefinition);
        const isAiAnalystField = isAiNonResearchField(fieldDefinition);

        return [
          {
            isDisabled: !!existingValueFieldMapping,
            allowedOverrideOptions: isAiResearchField
              ? ['ifChanged']
              : defaultOverrideOptions,
            partialFieldMapping: {
              customFieldId: id,
              kind: 'custom' as const,
              type: 'value' as const,
            },
            label: label,
            value: `${id.toString()}-value`,
          } satisfies FieldMappingOption,
          ...(isAiAnalystField
            ? [
                {
                  isDisabled: !!existingReasoningFieldMapping,
                  allowedOverrideOptions: ['ifChanged'],
                  partialFieldMapping: {
                    customFieldId: id,
                    kind: 'custom' as const,
                    type: 'reasoning' as const,
                  },
                  label: label + ' Reasoning',
                  value: `${id.toString()}-reasoning`,
                } satisfies FieldMappingOption,
              ]
            : []),
        ];
      }),
  ]
    .flat()
    .sort((a, b) => a.label.localeCompare(b.label));
}

type BaseOption = {
  isDisabled: boolean;
  label: string;
  value: string;
};
type FieldMappingOption = BaseOption & {
  allowedOverrideOptions: FieldOverride[];
  partialFieldMapping:
    | Pick<
        AdditionalKeyplayFieldMapping,
        'kind' | 'enrichmentField' | 'marketId'
      >
    | Pick<CustomFieldMapping, 'kind' | 'customFieldId' | 'type'>;
};

const defaultOverrideOptions = ['always', 'ifEmpty'] satisfies FieldOverride[];

type OverrideSelectOption = LabelAndDescriptionData<FieldOverride>;
const overrideOptions = [
  {
    value: 'always',
    label: 'Always override',
    description: 'Always override this field',
  },
  {
    value: 'ifEmpty',
    label: 'No override',
    description: 'Only write to this field if blank',
  },
  {
    value: 'ifChanged',
    label: 'If changed',
    description: 'Only override if the value has changed',
  },
] satisfies OverrideSelectOption[];

const FieldMappingGridRow = ({
  crmFieldOptions,
  fieldMappingOptions,
  selectedFieldMapping,
}: {
  crmFieldOptions: BaseOption[];
  fieldMappingOptions: FieldMappingOption[];
  selectedFieldMapping:
    | AdditionalFieldMapping
    | PlaceholderAdditionalFieldMapping;
}) => {
  const integrationInfo = useIntegrationStaticInfo();
  const {
    deleteAdditionalFieldMapping,
    updateAdditionalFieldMapping,
    publishChanges,
    isSaveableFieldMapping,
  } = useIntegrationDetailsState();
  const isPlaceholderFieldMapping =
    isPlaceholderAdditionalFieldMapping(selectedFieldMapping);

  const selectedFieldMappingOption = isPlaceholderFieldMapping
    ? undefined
    : fieldMappingOptions.find((option) =>
        _.isMatch(selectedFieldMapping, option.partialFieldMapping)
      );

  return (
    <>
      <GridCell p={0} justifyContent={'center'}>
        <Button
          colorScheme="red"
          fontSize={14}
          onClick={() => {
            deleteAdditionalFieldMapping(selectedFieldMapping.id);
            if (isSaveableFieldMapping(selectedFieldMapping)) {
              publishChanges.mutate();
            }
          }}
          variant="outline"
        >
          <TrashCan />
        </Button>
      </GridCell>

      <GridCell p={0}>
        <Select
          hideSelectedOptions={true}
          menuPlacement="top"
          onChange={(newFieldMappingOption) => {
            if (!newFieldMappingOption) {
              return;
            }

            if (isPlaceholderFieldMapping) {
              updateAdditionalFieldMapping({
                id: selectedFieldMapping.id,
                crmFieldName: '',
                managementType: 'additional',
                // Select the first allowed override option.
                override: newFieldMappingOption.allowedOverrideOptions[0],
                updatedAt: new Date(),
                ...newFieldMappingOption.partialFieldMapping,
              });
            } else {
              const shouldUpdateOverride =
                !newFieldMappingOption.allowedOverrideOptions.includes(
                  selectedFieldMapping.override
                );

              updateAdditionalFieldMapping({
                ...selectedFieldMapping,
                ...newFieldMappingOption.partialFieldMapping,
                ...(shouldUpdateOverride
                  ? // If there's no overlap between the allowed override
                    // options, select the first allowed option.
                    {override: newFieldMappingOption.allowedOverrideOptions[0]}
                  : {}),
              });
              publishChanges.mutate();
            }
          }}
          options={fieldMappingOptions}
          styles={{
            container: (base) => ({
              ...base,
              flexGrow: 1,
            }),
          }}
          value={selectedFieldMappingOption}
        />
      </GridCell>
      <GridCell>
        <ChevronRight />
      </GridCell>
      <GridCell p={0}>
        <Select
          hideSelectedOptions={true}
          menuPlacement="top"
          isDisabled={isPlaceholderFieldMapping}
          onChange={(selectedOption) => {
            if (!selectedOption || isPlaceholderFieldMapping) {
              return;
            }

            updateAdditionalFieldMapping({
              ...selectedFieldMapping,
              crmFieldName: selectedOption.value,
            });
            publishChanges.mutate();
          }}
          options={crmFieldOptions}
          placeholder={`Select a ${integrationInfo.label} field`}
          styles={{
            container: (base) => ({
              ...base,
              flexGrow: 1,
            }),
          }}
          value={crmFieldOptions.find((option) => {
            return (
              !isPlaceholderFieldMapping &&
              option.value === selectedFieldMapping.crmFieldName
            );
          })}
        />
      </GridCell>
      <GridCell p={0} ms="4">
        <Select
          components={{
            Option: LabelAndDescriptionOption<FieldOverride>,
          }}
          isDisabled={isPlaceholderFieldMapping}
          isMulti={false}
          isSearchable={false}
          menuPlacement="top"
          options={
            selectedFieldMappingOption
              ? overrideOptions.filter((option) =>
                  selectedFieldMappingOption.allowedOverrideOptions.includes(
                    option.value
                  )
                )
              : []
          }
          placeholder={'Select...'}
          value={
            isPlaceholderFieldMapping
              ? undefined
              : overrideOptions.find(
                  (option) => option.value === selectedFieldMapping.override
                )
          }
          onChange={(selectedOption) => {
            if (!selectedOption || isPlaceholderFieldMapping) {
              return;
            }

            updateAdditionalFieldMapping({
              ...selectedFieldMapping,
              override: selectedOption.value,
            });
            publishChanges.mutate();
          }}
          styles={{
            container: (base) => ({
              ...base,
              flexGrow: 1,
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? '#EDF2FD' : '',
              color: 'inherit',
              ':hover': {
                backgroundColor: '#EDF2FD',
              },
            }),
          }}
        />
      </GridCell>
    </>
  );
};
